import { useInfiniteQuery } from '@tanstack/react-query';
import { getAuditEvents } from '@services/auditService';
import type { AuditFilter } from '@/types/audit';

const PAGE_SIZE = 20;

/**
 * Query-key stabiliser. The host might re-create the filter literal every
 * render; serialising to a string makes the key identity-stable.
 */
function filterToKey(f: AuditFilter): string {
  return JSON.stringify({
    rt: f.resourceType ?? null,
    ri: f.resourceId ?? null,
    at: f.actorType ?? null,
    ai: f.actorId ?? null,
    en: f.eventName ?? null,
    fd: f.fromDate ?? null,
    td: f.toDate ?? null,
    fm: f.filterMap ?? null,
  });
}

/**
 * Paginated audit-event list. `useInfiniteQuery` so the drawer can render
 * "Load more" and accumulate pages.
 *
 * @param filter   Watcher filter — passed verbatim to GET /v1/audits.
 * @param enabled  When false, the query stays idle (no fetch, no cache write).
 *                 The drawer passes its `open` state here so the query does
 *                 not run while the drawer is rendered-but-closed.
 *                 Combined with the AUDIT_READ permission check.
 */
export function useAuditEvents(filter: AuditFilter, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['audit-events', filterToKey(filter)],
    queryFn: ({ pageParam }) =>
      getAuditEvents(filter, pageParam as number, PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.data.length, 0);
      return loaded < lastPage.meta.total ? allPages.length + 1 : undefined;
    },
    // Defensive guard — modal hosts mount the component unconditionally and
    // pass an empty `resourceId` until the trigger fires. We require *some*
    // narrowing (either `resourceId` OR a non-empty `filterMap`) before
    // hitting the server; otherwise an accidentally-`enabled=true` call with
    // an empty filter would page through every audit event the user can see.
    enabled:
      enabled &&
      (!!filter.resourceId ||
        (!!filter.filterMap && Object.keys(filter.filterMap).length > 0)),
  });
}
